/**
 * Registry.jsx — Aba "Cadastro" do DetailPanel.
 *
 * Conteúdo:
 *   - Cartão de cadastro: SKU, Nome, Marca, Bling ID, Cód. fornecedor,
 *     Preço de custo (BR), Un. por pallet, Tag baixo estoque (editável).
 *   - Lista de MLBs vinculados (siblingMlbs em modo MLB, mlbs em modo SKU).
 */
import { useRef, useState } from 'react';
import { useToast } from '../../../../../components/ui/Toast';
import { api } from '../../../../../services/api';
import { TBtn } from '../../primitives';
import { useDetailPanel } from '../index.jsx';
import { CadastroField } from '../internals.jsx';

/**
 * Campo editável de "Limite de baixo estoque" exibido na aba Cadastro. Quando
 * o estoque Bling fica abaixo deste valor, a tag "baixo estoque" é atribuída
 * automaticamente. Persiste via PATCH /products/:identifier.
 */
function LowStockThresholdField({ row, T, onUpdateRow }) {
    const toast = useToast();
    const initial = row.low_stock_threshold ?? 20;
    const [value, setValue] = useState(String(initial));
    const [saving, setSaving] = useState(false);
    // `savedRef` armazena { sku, value }: só ressincroniza o input quando o
    // PRODUTO muda (DetailPanel é reusado pra SKUs diferentes), nunca quando
    // o threshold do mesmo produto mudou. Antes comparava só o valor: a `row`
    // prop é uma ref STALE (vem de `selected` em index.jsx que não atualiza
    // após save), então depois de salvar 10 a UI ainda via initial=20 e
    // forçava value→"20", impedindo o user de digitar "20" de novo (dirty=false).
    const savedRef = useRef({ sku: row.sku, value: initial });
    if (savedRef.current.sku !== row.sku) {
        savedRef.current = { sku: row.sku, value: initial };
        setValue(String(initial));
    }

    const dirty = String(savedRef.current.value) !== String(value).trim();

    const handleSave = async () => {
        const numeric = Math.max(0, Math.floor(Number(value)));
        if (!Number.isFinite(numeric)) {
            toast.error('Valor inválido');
            return;
        }
        setSaving(true);
        try {
            await api.updateProductLowStockThreshold(row.productId || row.sku, numeric);
            // Atualiza savedRef com o NOVO valor salvo — daqui pra frente, `dirty`
            // compara contra esse valor (não contra o prop stale).
            savedRef.current = { sku: row.sku, value: numeric };
            setValue(String(numeric));
            onUpdateRow?.(row.sku, { low_stock_threshold: numeric });
            toast.success('Tag "baixo estoque" atualizada');
        } catch (err) {
            toast.error(err?.message || 'Erro ao salvar');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr',
                padding: '8px 12px',
                fontSize: 12,
                background: T.bg,
                borderBottom: `1px solid ${T.line}`,
            }}
        >
            <span style={{ color: T.muted }}>Tag: baixo estoque</span>
            <div>
                <input
                    type="number"
                    min="0"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    style={{
                        padding: '4px 8px',
                        border: `1px solid ${T.line}`,
                        background: T.bg,
                        color: T.ink,
                        borderRadius: 4,
                        fontFamily: T.mono,
                        fontSize: 12,
                        width: 100,
                        outline: 'none',
                        marginRight: '6px',
                    }}
                />
                <TBtn variant="primary" size="xs" onClick={handleSave} disabled={saving || !dirty}>
                    {saving ? 'salvando…' : 'salvar'}
                </TBtn>
            </div>
        </div>
    );
}

export function Registry() {
    const { row, T, tab, onUpdateRow } = useDetailPanel();
    if (tab !== 'cadastro') return null;

    // Em row do tipo SKU, `row.mlbs` já tem todos os anúncios. Em row do tipo
    // MLB, `row.mlbs` é singleton (pra coluna 'mlb' não mostrar +N);
    // `siblingMlbs` preserva a lista completa do SKU.
    const linked = Array.isArray(row.siblingMlbs) && row.siblingMlbs.length > 0 ? row.siblingMlbs : row.mlbs || [];

    return (
        <div>
            <div
                style={{
                    border: `1px solid ${T.line}`,
                    borderRadius: 4,
                    overflow: 'hidden',
                    marginBottom: 14,
                }}
            >
                <CadastroField label="SKU" value={row.sku} T={T} />
                <CadastroField label="Nome" value={row.name} mono={false} uppercase T={T} />
                <CadastroField label="Marca" value={row.brand} mono={false} uppercase T={T} />
                <CadastroField label="Bling ID" value={row.bling_id ? String(row.bling_id) : null} T={T} />
                <CadastroField label="Cód. fornecedor" value={row.supplier_code} T={T} />
                {/* Preço de custo (DB: products.cost_price). Vem do row.cost
                    via mapeamento no useOperacoesData. Format BR: R$ X.XXX,YY. */}
                <CadastroField
                    label="Preço de custo"
                    value={
                        Number.isFinite(Number(row.cost)) && Number(row.cost) > 0
                            ? `R$ ${Number(row.cost).toLocaleString('pt-BR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                              })}`
                            : null
                    }
                    T={T}
                />
                <CadastroField
                    label="Un. por pallet"
                    value={
                        row.units_per_pallet !== null && row.units_per_pallet !== undefined
                            ? Number(row.units_per_pallet).toLocaleString('pt-BR')
                            : null
                    }
                    T={T}
                />
                <LowStockThresholdField row={row} T={T} onUpdateRow={onUpdateRow} />
            </div>

            <div
                style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    color: T.muted,
                    marginBottom: 8,
                }}
            >
                MLBs vinculados ({linked.length})
            </div>
            {linked.length === 0 ? (
                <div style={{ fontSize: 12, color: T.muted, padding: '8px 0' }}>
                    Nenhum MLB observado no período carregado.
                </div>
            ) : (
                <div
                    style={{
                        border: `1px solid ${T.line}`,
                        borderRadius: 4,
                        overflow: 'hidden',
                        marginBottom: 14,
                    }}
                >
                    {linked.map((mlb, i) => (
                        <div
                            key={mlb}
                            style={{
                                padding: '8px 12px',
                                fontSize: 12,
                                fontFamily: T.mono,
                                color: T.ink,
                                borderBottom: i < linked.length - 1 ? `1px solid ${T.line}` : 'none',
                                background: i % 2 === 0 ? T.bg : T.bgSoft,
                            }}
                        >
                            {mlb}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
